// ===========================================
// CÁLCULOS UNIVERSALES - MAPASAC
// Equivalente a archivo .PRG de VFP9
// Zero hardcode - Funciones reutilizables
// ===========================================

/**
 * Calcula precio de compra basado en precio lista y descuento
 * @param {number} prelista - Precio de lista
 * @param {number} descuento - Porcentaje de descuento (0-100)
 * @returns {number} Precio de compra con 4 decimales
 */
export const calcularPrecioCompra = (prelista, descuento = 0) => {
  const precioLista = parseFloat(prelista) || 0;
  const porcDescuento = parseFloat(descuento) || 0;
  
  const precompra = precioLista - (precioLista * porcDescuento / 100);
  return parseFloat(precompra.toFixed(4));  // ✅ 4 decimales
};

/**
 * Calcula precio de venta NETO (con IVA incluido)
 * @param {number} precompra - Precio de compra
 * @param {number} utilidad - Porcentaje de utilidad (0-500)
 * @param {number} tasaIVA - Tasa de IVA en decimal (0.16 para 16%)
 * @returns {number} Precio NETO con IVA con 4 decimales
 */
export const calcularPrecioVenta = (precompra, utilidad = 0, tasaIVA = 0.16) => {
  const precioCompra = parseFloat(precompra) || 0;
  const porcUtilidad = parseFloat(utilidad) || 0;
  const tasa = parseFloat(tasaIVA) || 0;
  
  // Paso 1: Calcular precio base sin IVA
  const precioBase = precioCompra * (1 + porcUtilidad / 100);
  
  // Paso 2: Agregar IVA al precio base
  const precioNeto = precioBase * (1 + tasa);
  
  return parseFloat(precioNeto.toFixed(4));  // ✅ 4 decimales
};

/**
 * Calcula precios completos de producto (compra y venta NETO con IVA)
 * @param {number} prelista - Precio de lista
 * @param {number} descuento - Porcentaje de descuento
 * @param {number} utilidad - Porcentaje de utilidad
 * @param {number} tasaIVA - Tasa de IVA en decimal (0.16 para 16%)
 * @returns {Object} { precompra, precioBase, precioNeto, ivaMonto }
 */
export const calcularPreciosProducto = (prelista, descuento = 0, utilidad = 0, tasaIVA = 0.16) => {
  const precompra = calcularPrecioCompra(prelista, descuento);
  const precioBase = precompra * (1 + utilidad / 100);
  const precioNeto = calcularPrecioVenta(precompra, utilidad, tasaIVA);
  const ivaMonto = precioNeto - precioBase;
  
  return { 
    precompra,      // Precio de compra
    precioBase,     // Precio sin IVA
    precioNeto,     // Precio con IVA (va al campo "precio")
    ivaMonto        // Monto de IVA aplicado
  };
};

/**
 * Desglose de IVA desde precio NETO (reversa)
 * Útil para reportes y facturas
 * @param {number} precioNeto - Precio con IVA incluido
 * @param {number} tasaIVA - Tasa de IVA en decimal (0.16 para 16%)
 * @returns {Object} { subtotal, ivaMonto }
 */
export const desgloseIVA = (precioNeto, tasaIVA = 0.16) => {
  const precio = parseFloat(precioNeto) || 0;
  const tasa = parseFloat(tasaIVA) || 0;
  
  const subtotal = precio / (1 + tasa);
  const ivaMonto = precio - subtotal;
  
  return {
    subtotal: parseFloat(subtotal.toFixed(4)),
    ivaMonto: parseFloat(ivaMonto.toFixed(4))
  };
};

/**
 * Valida que un precio sea mayor que zero
 * @param {number} precio - Precio a validar
 * @returns {boolean} true si es válido
 */
export const validarPrecio = (precio) => {
  const valor = parseFloat(precio);
  return !isNaN(valor) && valor > 0;
};

/**
 * Valida que un porcentaje esté en rango válido
 * @param {number} porcentaje - Porcentaje a validar
 * @param {number} min - Valor mínimo (default: 0)
 * @param {number} max - Valor máximo (default: 100)
 * @returns {boolean} true si es válido
 */
export const validarPorcentaje = (porcentaje, min = 0, max = 100) => {
  const valor = parseFloat(porcentaje);
  return !isNaN(valor) && valor >= min && valor <= max;
};

/**
 * Formatea número a moneda (pesos mexicanos)
 * @param {number} valor - Valor a formatear
 * @returns {string} Valor formateado (ej: "$1,234.56")
 */
export const formatearMoneda = (valor) => {
  const numero = parseFloat(valor) || 0;
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(numero);
};

/**
 * Formatea número a porcentaje
 * @param {number} valor - Valor a formatear
 * @param {number} decimales - Decimales a mostrar (default: 2)
 * @returns {string} Valor formateado (ej: "15.50%")
 */
export const formatearPorcentaje = (valor, decimales = 2) => {
  const numero = parseFloat(valor) || 0;
  return `${numero.toFixed(decimales)}%`;
};

// ===========================================
// VALIDACIONES DINÁMICAS
// ===========================================

/**
 * Evalúa reglas de validación custom de forma segura
 * Usado por SqlexecBuilder para validaciones schema-driven
 * 
 * @param {string} rule - Expresión a evaluar (ej: "precio > precompra")
 * @param {Object} formData - Datos del formulario
 * @returns {boolean} true si la regla se cumple, false si no
 * 
 * @example
 * evaluateValidationRule("precio > precompra", { precio: 100, precompra: 90 })
 * // returns true
 * 
 * evaluateValidationRule("precio > precompra", { precio: 80, precompra: 90 })
 * // returns false
 */
export const evaluateValidationRule = (rule, formData) => {
  try {
    // Reemplazar nombres de campos con sus valores numéricos
    let expression = rule;
    
    // Extraer nombres de campos (palabras que no son operadores ni keywords)
    const fieldMatches = rule.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) || [];
    
    fieldMatches.forEach(match => {
      // Skip palabras reservadas de JavaScript
      if (['true', 'false', 'null', 'undefined'].includes(match)) return;
      
      // Reemplazar con valor del formData
      if (formData.hasOwnProperty(match)) {
        const value = parseFloat(formData[match]) || 0;
        expression = expression.replace(
          new RegExp(`\\b${match}\\b`, 'g'), 
          value
        );
      }
    });
    
    // Evaluar expresión de forma segura
    // eslint-disable-next-line no-new-func
    return Function(`"use strict"; return (${expression})`)();
    
  } catch (err) {
    console.error('❌ Error evaluando regla de validación:', err);
    console.error('Regla:', rule);
    console.error('FormData:', formData);
    return true; // En caso de error, permitir (no bloquear usuario)
  }
};

/**
 * Valida que un campo sea mayor que otro campo (comparación dinámica)
 * 
 * @param {number} value - Valor del campo a validar
 * @param {string} compareFieldName - Nombre del campo con el que comparar
 * @param {Object} formData - Datos del formulario
 * @returns {boolean} true si value > compareValue
 * 
 * @example
 * validateGreaterThan(100, "precompra", { precompra: 90 })
 * // returns true
 */
export const validateGreaterThan = (value, compareFieldName, formData) => {
  const val = parseFloat(value);
  const compareVal = parseFloat(formData[compareFieldName]);
  
  if (isNaN(val) || isNaN(compareVal)) return true; // No bloquear si hay NaN
  
  return val > compareVal;
};

// ===========================================
// FORMATO NUMÉRICO DESKTOP (Oracle/SAP/Dynamics)
// ===========================================

/**
 * Formatea número estilo desktop con separadores de miles
 * @param {number|string} value - Valor a formatear
 * @param {number} decimals - Decimales fijos (default: 4)
 * @returns {string} "6,952.5500"
 */
export const formatNumberDesktop = (value, decimals = 4) => {
  if (value === '' || value === null || value === undefined) return '';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  const fixed = num.toFixed(decimals);
  const [integer, decimal] = fixed.split('.');
  const withCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return decimal ? `${withCommas}.${decimal}` : withCommas;
};

/**
 * Parse número desktop a número puro
 * @param {string} formattedValue - "6,952.5500"
 * @returns {number} 6952.55
 */
export const parseNumberDesktop = (formattedValue) => {
  if (!formattedValue) return 0;
  const cleaned = formattedValue.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/**
 * Handler para input numérico con formato automático
 * @param {string} rawValue - Valor del input
 * @param {number} decimals - Decimales permitidos
 * @param {number} max - Valor máximo
 * @returns {object} { displayValue, numericValue, isValid }
 */
export const handleNumberInputDesktop = (rawValue, decimals = 4, max = 9999.9999) => {
  let cleaned = rawValue.replace(/[^\d.,]/g, '');
  const withoutCommas = cleaned.replace(/,/g, '');
  
  if (withoutCommas && !/^\d*\.?\d*$/.test(withoutCommas)) {
    return { displayValue: '', numericValue: 0, isValid: false };
  }
  
  if (withoutCommas.includes('.')) {
    const [integer, decimal] = withoutCommas.split('.');
    cleaned = decimal ? `${integer}.${decimal.substring(0, decimals)}` : `${integer}.`;
  } else {
    cleaned = withoutCommas;
  }
  
  const numericValue = parseFloat(cleaned) || 0;
  
  if (numericValue > max) {
    return { 
      displayValue: formatNumberDesktop(max, decimals), 
      numericValue: max, 
      isValid: false 
    };
  }
  
  return {
    displayValue: cleaned,
    numericValue: numericValue,
    isValid: true
  };
};

// ===========================================
// FUNCIONES FUTURAS (PLACEHOLDER)
// ===========================================

/**
 * TODO: Calcular impuestos compuestos (IEPS + IVA)
 * Para usar con tabla invetasa
 * @param {number} precio - Precio base
 * @param {Array} impuestos - Array de impuestos a aplicar
 * @returns {Object} { subtotal, impuestos, total }
 */
export const calcularImpuestos = (precio, impuestos = []) => {
  // Implementar cuando tengamos el TAB de impuestos compuestos
  return { subtotal: precio, impuestos: 0, total: precio };
};

/**
 * TODO: Calcular descuento por volumen
 */
export const calcularDescuentoVolumen = (cantidad, precioUnitario) => {
  // Implementar lógica de descuentos escalonados
  return precioUnitario;
};

// ===========================================
// 🆕 CÁLCULOS DE COTIZACIONES/PEDIDOS/FACTURAS
// ===========================================

// IVA de un renglon con el MISMO redondeo que numeric(13,2) de PostgreSQL (mitad
// alejandose del cero) sobre el valor de 4 decimales: es lo que se guarda en
// clie*de.impiva y lo que usa el XML. Aritmetica entera para no depender del
// redondeo binario de toFixed (1.005 -> 1.01, -2.675 -> -2.68).
const redondearComoNumeric2 = (valor4) => {
  const n4 = Math.round(Math.abs(valor4) * 1e4);
  const n2 = Math.floor((n4 + 50) / 100);
  return (valor4 < 0 ? -n2 : n2) / 100;
};

/**
 * Calcula totales de una partida
 * Preview en frontend - Backend recalcula al guardar
 * 
 * @param {Object} partida - Datos de la partida
 * @param {number} partida.cantidad - Cantidad
 * @param {number} partida.precio - Precio unitario
 * @param {number} partida.porcentaje_iva - Tasa IVA (16.00 para 16%)
 * @returns {Object} { importe, impiva, imptotal }
 */
export const calcularTotalesPartida = (partida, cotizacion_esbase = true, tipocamb_valor = 1, cantidad_override = null, c_regimenfiscal = '000') => {
  const cantidad = parseFloat(cantidad_override ?? partida.cantidad) || 0;
  const tc = parseFloat(tipocamb_valor) || 1;
  const precioBase = parseFloat(partida.precio) || 0;
  const precio = (cotizacion_esbase !== partida.esbase && tc > 1)
    ? (cotizacion_esbase ? precioBase * tc : precioBase / tc)
    : precioBase;
  const tasa_iva = parseFloat(partida.porcentaje_iva) || 0;
  const pordesc   = parseFloat(partida.pordesc) || 0;  
  
  // Base = subtotal sin impuestos
  const importe_bruto = cantidad * precio;
  const impdesc = parseFloat((importe_bruto * (pordesc / 100)).toFixed(4));
  const importe = importe_bruto - impdesc;

  const tasas_extra = Array.isArray(partida.tasas_extra) ? partida.tasas_extra : [];
  //console.log('🔍 idinvearts:', partida.idinvearts, '| tasas_extra:', JSON.stringify(tasas_extra));
  //console.log('🔍 c_regimenfiscal:', c_regimenfiscal);
  
  // ← filtrar por régimen del cliente
  const tasasAplicables = tasas_extra.filter(tasa => {
    const aplica = Array.isArray(tasa.aplica_regimen) ? tasa.aplica_regimen : [];
    if (aplica.length === 0) return true;
    return aplica.includes(parseInt(c_regimenfiscal));
  });

  let impuestos_extra = [];
  let ieps_importe = 0;
  let locales_importe = 0;
  
  tasasAplicables.forEach(tasa => {
    const importe_tasa = parseFloat((importe * parseFloat(tasa.tasaocuota)).toFixed(4));
    if (tasa.nodo_xml === 'Traslados') ieps_importe += importe_tasa;
    if (tasa.nodo_xml === 'RetencionesLocales') locales_importe += importe_tasa;
    impuestos_extra.push({
      idsattasocuo: tasa.idsattasocuo,
      descripcio:   tasa.descripcio,
      base:         parseFloat(importe.toFixed(4)),
      tasaocuota:   parseFloat(parseFloat(tasa.tasaocuota).toFixed(6)),
      importe:      importe_tasa,
      retencion:    tasa.retencion,
      traslado:     tasa.traslado,
      nodo_xml:     tasa.nodo_xml
    });
  });

  // 🔥 IVA sobre importe + IEPS
  const base_iva = importe + ieps_importe;
  const impiva = base_iva * (tasa_iva / 100);
  const imptotal = importe;
  
  // console.log('🔍 calcularTotalesPartida:', {
  //   cantidad, precio, tasa_iva,
  //   importe, ieps_importe, base_iva,
  //   impiva, tasas_extra_count: tasas_extra.length
  // });
  
  return {
    precio:          parseFloat(precio.toFixed(4)),
    importe_bruto:   parseFloat(importe_bruto.toFixed(2)),
    importe:         parseFloat(importe.toFixed(4)),
    impdesc:         parseFloat(impdesc.toFixed(4)),
    impiva:          redondearComoNumeric2(parseFloat(impiva.toFixed(4))),
    imptotal:        parseFloat(imptotal.toFixed(2)),
    impuestos_extra: impuestos_extra
  };
};

/**
 * Calcula totales generales del documento (cotización/pedido/factura)
 * Preview en frontend - Backend recalcula al guardar
 * 
 * @param {Array} partidas - Array de partidas
 * @returns {Object} Totales generales
 */
export const calcularTotalesGenerales = (partidas) => {
  let importe = 0;
  let descuento = 0;
  let iva = 0;
  let ieps = 0;
  let retencion_iva = 0;
  let retencion_isr = 0;
  let otros_traslados = 0;
  let otras_retenciones = 0;

  partidas.forEach(partida => {
    importe += parseFloat(partida.imptotal) || 0;
    descuento += parseFloat(partida.impdesc) || 0;
    // Suma el IVA ya redondeado como se guarda por renglon: encabezado = suma de renglones = XML
    iva += redondearComoNumeric2(parseFloat(partida.impiva) || 0);

    // 🔥 Motor universal de tasas extra
    //const extras = Array.isArray(partida.impuestos_extra) ? partida.impuestos_extra : [];
    const extras = Array.isArray(partida.impuestos_extra) 
      ? partida.impuestos_extra : Array.isArray(partida.tasas_extra) 
      ? partida.tasas_extra 
      : [];

    extras.forEach(tasa => {
      const imp = parseFloat(tasa.importe) || 0;
      if (tasa.retencion && !tasa.traslado) {
        // Retenciones → restan del total
        if (tasa.descripcio?.includes('ISR')) retencion_isr += imp;
        else if (tasa.descripcio?.includes('IVA')) retencion_iva += imp;
        else otras_retenciones += imp;
      } else if (tasa.traslado && !tasa.retencion) {
        // Traslados extra → suman al total
        if (tasa.descripcio?.includes('IEPS')) ieps += imp;
        else otros_traslados += imp;
      }
    });
  });

  const subtotal = importe - descuento;
  const subtotal_con_iva = subtotal + iva;
  const total = subtotal_con_iva + ieps + otros_traslados - retencion_iva - retencion_isr - otras_retenciones;

  return {
    importe:          parseFloat(importe.toFixed(2)),
    descuento:        parseFloat(descuento.toFixed(2)),
    subtotal:         parseFloat(subtotal.toFixed(2)),
    iva:              parseFloat(iva.toFixed(2)),
    subtotal_con_iva: parseFloat(subtotal_con_iva.toFixed(2)),
    ieps:             parseFloat(ieps.toFixed(2)),
    otros_traslados:  parseFloat(otros_traslados.toFixed(2)),
    retencion_iva:    parseFloat(retencion_iva.toFixed(2)),
    retencion_isr:    parseFloat(retencion_isr.toFixed(2)),
    otras_retenciones:parseFloat(otras_retenciones.toFixed(2)),
    total:            parseFloat(total.toFixed(2))
  };
};